import { useApiQuery } from '../useApiQuery';
import { apiGet, operationsClient } from '@/lib/api';
import { executiveReportEndpoints } from '@/lib/api/endpoints';
import { executiveReportKeys } from '@/lib/queryKeys';
import type { ExecutiveSummary } from '@/components/dashboard/dashboardWidgets';
import { useCompanyTimezone } from '@/hooks/settings/useCompanyTimezone';
import { dateFilterToRange } from './dashboardRange';
import { type DateFilter } from './useDashboardStats';

interface UseExecutiveSummaryOptions<TData> {
  select?: (summary: ExecutiveSummary) => TData;
  enabled?: boolean;
}

/**
 * The Home executive summary — the single source for both the KPI tiles and the
 * Revenue Trend chart. Because the range is stabilized (see `dateFilterToRange`)
 * and every caller shares this query key, React Query fetches it once per
 * window. Never retries a 429 (the rate-limiter), so a throttle doesn't snowball.
 */
export function useExecutiveSummary<TData = ExecutiveSummary>(
  filter: DateFilter,
  options: UseExecutiveSummaryOptions<TData> = {},
) {
  const timeZone = useCompanyTimezone();
  const range = dateFilterToRange(filter, new Date(), timeZone);
  return useApiQuery<ExecutiveSummary, TData>({
    queryKey: executiveReportKeys.report('summary', range),
    queryFn: () =>
      apiGet<ExecutiveSummary>(operationsClient, executiveReportEndpoints.summary(range)),
    select: options.select,
    enabled: options.enabled,
    showErrorToast: false,
    staleTime: 30_000,
    retry: (count, error) => error.status !== 429 && count < 2,
  });
}
